import { CommonModule, CurrencyPipe } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { RouterModule } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedSingleEventRegistrationsCount } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { BillingService, roundMoney } from '@app/modules/payments/services/billing.service'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { ExportStatus } from '@app/modules/workspaces/models/export-status.interface'
import { PlanPrice } from '@app/modules/workspaces/models/plan-price.interface'
import { BillingPaymentMethod } from '@app/v2/settings/components/billing-payments/billing/payment-cards-modal/payment-cards-modal.component'
import { WalletTopUpModalComponent } from '@app/v2/settings/components/billing-payments/billing/wallet-top-up-modal/wallet-top-up-modal.component'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { NavbarService } from '@app/v2/shared/components/navbar/navbar.service'
import { listenLayeredOverlayDismiss } from '@app/v2/shared/helpers/layered-overlay-dismiss'
import { RegistrationsDataSource } from '@database/models/registrations.datasource'
import { DatabaseService, Order, OrderByForms, OrderByRegistrations, OrderTypes } from '@database/services/database.service'
import { NbButtonModule, NbCardModule, NbDialogRef, NbSpinnerModule } from '@nebular/theme'
import { forkJoin, Observable, of, Subject, timer } from 'rxjs'
import { catchError, concatMap, expand, map, take, takeUntil, tap } from 'rxjs/operators'
import { ExportSectionComponent } from './components/export-section/export-section.component'
import { SettleLineComponent } from './components/settle-line/settle-line.component'
import { Record, SettleTableComponent } from './components/settle-table/settle-table.component'
/**
 * Reduces a plan price interface to it's id and amount
 */
interface ReducedPlanPrice {
  /**
   * Plan price id
   */
  id: string
  /**
   * Plan price amount
   */
  amount: number
}

/**
 * Defines the prices available for a certain plan
 */
interface PlanPrices {
  /** Virtual plan price */
  virtual: ReducedPlanPrice
  /** Physical plan price */
  physical: ReducedPlanPrice
  /** Difference between Virtual and Physical plan price */
  difference: ReducedPlanPrice
}
/**
 * Defines available plan prices for Basic and Pro plans
 */
interface Plans {
  /**
   * Basic plan prices
   */
  Basic: PlanPrices
  /**
   * Pro plan prices
   */
  Pro: PlanPrices
}

type SettleFlowStep = 'settle' | 'topup'

/**
 * Settles registrations and starts an export process upon settling or if requested without settling
 */
@Component({
  selector: 'ngx-settle-registrants',
  templateUrl: './settle-registrants.component.html',
  styleUrls: ['./settle-registrants.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    NbCardModule,
    NbButtonModule,
    SettleLineComponent,
    ExportSectionComponent,
    CurrencyPipe,
    NbSpinnerModule,
    SettleTableComponent,
    RouterModule,
    WalletTopUpModalComponent
  ]
})
export class SettleRegistrantsComponent implements OnInit, OnDestroy {
  /**
   * Stops all hot observables
   */
  _destroy$ = new Subject()
  private unlistenLayeredOverlayDismiss?: () => void
  /** On destroy implementation */
  ngOnDestroy() {
    this.unlistenLayeredOverlayDismiss?.()
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /** Determines whether the component is performing an async operation */
  processing = true
  /** Determines whether the workspace is currently exporting data */
  exporting = false
  /** Hold the export status for current event */
  exportStatus: ExportStatus = null
  /** Registration data source used in orders by registrations */
  source: RegistrationsDataSource

  /** Order sent to backend, either by registrations or by selected forms */
  order: OrderByRegistrations | OrderByForms
  /** Records displayed in the frontend which can be toggled in case of settling by forms */
  records: Record[] = []
  /** Component is in registration settling mode? */
  registrantMode = false
  /** Count of settled registrations */
  settled = 0
  /** Count of unsettled registrations */
  unsettled = 0
  /** Count of selected registrations */
  selected = 0
  /** Available plans */
  plans: Plans
  /** Current workspace plan */
  currentPlan: string
  /** Current customer balance */
  customerBalance: number
  /** Inline flow step: settle summary or wallet top-up */
  step: SettleFlowStep = 'settle'
  /** Loading saved cards before the top-up step */
  loadingPaymentMethods = false
  /** Saved cards for inline wallet top-up */
  paymentMethods: BillingPaymentMethod[] = []
  /** Event id */
  eventId: number
  /** Selected form slug */
  selectedForm: string = null

  settledForms = []
  settledTotal = 0
  /**
   * Creates the component and injects it's dependencies
   * @param dialogRef DialogRef
   * @param service DatabaseService
   * @param auth AuthService
   * @param navbarService NavbarService
   * @param billing BillingService
   * @param eventService EventService
   */
  constructor(
    private dialogRef: NbDialogRef<SettleRegistrantsComponent>,
    private service: DatabaseService,
    private auth: AuthService,
    private navbarService: NavbarService,
    private billing: BillingService,
    private eventService: EventService,
    private personaService: PersonaService,
    private _organizationService: OrganizationService
  ) {}

  /**
   * Open organization settings billing in a new tab (e.g. plan upgrade).
   */
  public openBilling(): void {
    this.auth.openOrganizationBillingInNewTab()
    this.close()
  }

  /**
   * Amount still needed in the wallet to cover the active settlement total.
   */
  get balanceShortfallUsd(): number {
    return Math.max(0, roundMoney(this.roundedTotalPrice - this.roundedCustomerBalance))
  }

  /** Whether the wallet cannot cover the active settlement total. */
  get insufficientBalance(): boolean {
    return this.roundedTotalPrice > 0 && this.balanceShortfallUsd > 0
  }

  /**
   * Suggested top-up for the wallet: shortfall floored at $50.
   */
  suggestedTopUpAmount(): number {
    return Math.max(50, this.balanceShortfallUsd)
  }

  /** Rounded wallet balance used for settlement checks.
   */
  get roundedCustomerBalance(): number {
    return roundMoney(this.customerBalance ?? 0)
  }

  /** Rounded total for active settlement records. */
  get roundedTotalPrice(): number {
    return roundMoney(this.totalPrice)
  }

  /**
   * Primary settle action — settle only when the wallet can cover the cost.
   */
  onSettleClick(): void {
    if (!this.canSettleProceed) return
    this.settle()
  }

  /**
   * Pay and export — top up when balance is insufficient, otherwise settle and export.
   */
  onPayExportClick(): void {
    if (this.insufficientBalance) {
      this.goToTopUpStep()
      return
    }
    if (!this.canPayExportProceed) return
    this.payAndExport()
  }

  /**
   * Load payment methods and navigate to the inline top-up step.
   */
  goToTopUpStep(): void {
    if (this.loadingPaymentMethods) return

    this.loadingPaymentMethods = true
    this._organizationService
      .getOrganizationPaymentMethods(this.billing.workspaceId)
      .pipe(
        catchError(() => of([] as BillingPaymentMethod[])),
        take(1),
        takeUntil(this._destroy$)
      )
      .subscribe((methods) => {
        this.paymentMethods = methods
        this.loadingPaymentMethods = false
        this.step = 'topup'
      })
  }

  /**
   * Return from top-up to the settle summary.
   */
  backFromTopUp(): void {
    this.step = 'settle'
  }

  /**
   * After a successful top-up, return to settle and refresh wallet balance.
   */
  public onWalletTopUpSuccess(amount: number): void {
    const updatedBalance = roundMoney(this.roundedCustomerBalance + amount)
    this.billing.publishWalletBalance(updatedBalance)
    this.customerBalance = updatedBalance
    this.step = 'settle'
    this._refreshCustomerBalance(updatedBalance)
  }

  /**
   * Refresh wallet balance from billing after top-up.
   * @param {number} [optimisticBalanceUsd] Balance to keep if the API response is stale.
   */
  private _refreshCustomerBalance(optimisticBalanceUsd?: number): void {
    this.billing
      .customer()
      .pipe(
        map((customer: { data: { balance: number } }) => customer?.data?.balance ?? 0),
        take(1),
        takeUntil(this._destroy$)
      )
      .subscribe((balance) => {
        this.customerBalance = optimisticBalanceUsd !== undefined ? Math.max(balance, optimisticBalanceUsd) : balance
      })
  }

  /**
   * OnInit implementation
   * Steps:
   * Check whether the user has an export running or not
   * If the user has an export running display the export progress
   * Otherwise display the requesting settling mode
   * Generate the order
   * Generate the records
   */
  ngOnInit(): void {
    this.unlistenLayeredOverlayDismiss = listenLayeredOverlayDismiss({
      closeParent: () => this.close()
    })
    this.eventService
      .checkExportProgress(this.source.initializeRequest.event)
      .pipe(takeUntil(this._destroy$))
      .subscribe((exportStatus: ExportStatus) => {
        this.exportStatus = exportStatus
        if (exportStatus.status === 'running') {
          this.exporting = true
          this.processing = false
          this.autoCheckProgress()
        } else {
          forkJoin([
            this.auth.getPlansWithPrices().pipe(
              map((plans: PlanPrice[]) => {
                const basic = plans.find((o) => o.id === 1)
                const pro = plans.find((o) => o.id === 2)
                const basicMap = this.generatePriceMap(basic)
                const proMap = this.generatePriceMap(pro)

                return {
                  Basic: basicMap,
                  Pro: proMap
                }
              }),
              takeUntil(this._destroy$)
            ),
            this.navbarService.getBalance(this.auth.currentWorkspace.id.toString()).pipe(takeUntil(this._destroy$)),
            this.registrantMode ? of(null) : this.service.getExportableCountsForEvent(this.source.initializeRequest.event),
            this.registrantMode ? of([]) : this.eventService.getEventForms(this.eventId)
          ])
            .pipe(takeUntil(this._destroy$))
            .subscribe(([plans, balance, exportableCounts, personas]: [Plans, number, ServerRepresentedSingleEventRegistrationsCount, Persona[]]) => {
              this.customerBalance = balance
              this.billing.publishWalletBalance(balance)
              this.plans = plans
              this.currentPlan = this.auth.plan

              if (this.registrantMode) {
                this.settled = this.source.selectedRegistrants.filter((o) => o.tags.settled).length
                this.unsettled = this.source.selectedRegistrants.filter((o) => !o.tags.settled).length
                this.selected = this.source.selectedRegistrants.length
                this.order = this.generateOrders()
                this.records = this.generateRecords()
              } else {
                this.settled = Number(exportableCounts.settledCount)
                this.unsettled = Number(exportableCounts.unsettledCount)
                this.selected = Number(exportableCounts.totalCount)
                this.order = this.generateFullOrder(exportableCounts, personas)
                this.records = this.generateFullRecords(exportableCounts, personas)
              }
              this.processing = false
            })
        }
      })
  }
  /**
   * Creates plan prices mapping for a workspace plan
   * @param plan PlanPrice @see PlanPrice
   * @returns PlanPrices @see PlanPrices
   */
  generatePriceMap(plan: PlanPrice): PlanPrices {
    return {
      physical: {
        id: plan.defaultPrice.id,
        amount: plan.defaultPrice.unit_amount
      },
      difference: {
        id: plan.diffrancePrice.id,
        amount: plan.diffrancePrice.unit_amount
      },
      virtual: {
        id: plan.virtualPrice.id,
        amount: plan.virtualPrice.unit_amount
      }
    }
  }
  /**
   * Closes the dialog reference (modal)
   */
  close() {
    this.dialogRef.close()
  }
  /**
   * Generates orders in case of registrantMode @see registrantMode
   * @returns OrderByRegistrations
   */
  generateOrders(): OrderByRegistrations {
    const virtualOrders = this.source.selectedRegistrants.filter((o) => o.settledStatus === OrderTypes.VIRTUAL && !o.tags.settled).map((o) => o.id)
    const physicalOrders = this.source.selectedRegistrants.filter((o) => o.settledStatus === OrderTypes.PHYSICAL && !o.tags.settled).map((o) => o.id)
    const differenceOrders = this.source.selectedRegistrants.filter((o) => o.settledStatus === OrderTypes.DIFFERENCE && !o.tags.settled).map((o) => o.id)
    const orders: Order[] = []
    const currentPlan = this.auth.plan
    if (virtualOrders.length) {
      orders.push({
        price_id: this.plans[currentPlan].virtual.id,
        type: OrderTypes.VIRTUAL,
        ids: virtualOrders
      })
    }
    if (physicalOrders.length) {
      orders.push({
        price_id: this.plans[currentPlan].physical.id,
        type: OrderTypes.PHYSICAL,
        ids: physicalOrders
      })
    }
    if (differenceOrders.length) {
      orders.push({
        price_id: this.plans[currentPlan].difference.id,
        type: OrderTypes.DIFFERENCE,
        ids: differenceOrders
      })
    }
    return {
      form_id: this.source.form.id,
      orders: orders
    }
  }
  /**
   * Generates records in case of a registrantMode @see registrantMode
   * @returns Record[]
   */
  generateRecords(): Record[] {
    const currentPlan = this.auth.plan
    const breakdownRecords: Record[] = (this.order as OrderByRegistrations).orders.map((order) => {
      const quantity = order.ids?.length ?? 0
      const price = this.plans[currentPlan][order.type].amount
      const priceCompared = this.plans.Pro[order.type].amount
      return {
        title: `${this.source.form.name} ${this.getTitle(order)}`,
        id: this.source.form.id,
        canBeActivated: false,
        active: false,
        selected: quantity,
        unsettled: quantity,
        eachAt: price / 100,
        eachAtCompared: priceCompared / 100,
        price: (quantity * price) / 100,
        priceCompared: (quantity * priceCompared) / 100
      }
    })
    const selectedCount = breakdownRecords.reduce((p, c) => p + (c.selected || 0), 0)
    const mainRecord: Record = {
      title: `${this.source.form.name}`,
      id: this.source.form.id,
      canBeActivated: false,
      active: true,
      selected: selectedCount || this.selected,
      unsettled: selectedCount || this.unsettled,
      price: breakdownRecords.reduce((p, c) => p + c.price, 0),
      priceCompared: breakdownRecords.reduce((p, c) => p + c.priceCompared, 0),
      breakdown: breakdownRecords,
      viewBreakdown: false
    }
    return [mainRecord]
  }
  /**
   * Gets the attached title to an order record based on it's order type
   * @param order Order
   * @returns string
   */
  getTitle(order: Order) {
    return order.type === OrderTypes.VIRTUAL ? '(Registrant)' : order.type === OrderTypes.PHYSICAL ? '(Attendee)' : '(Upgraded Attendee)'
  }
  /**
   * Settles registrations by firing a registrant order or a forms order
   * Then starts an export process
   * Or immediately starts an export process if no records are selected (no order)
   * @returns void
   */
  payAndExport() {
    this.processing = true
    if (this.unsettled > 0 && this.records.some((o) => o.active)) {
      if (this.registrantMode) {
        this.service
          .settleByRegistrations(this.order as OrderByRegistrations)
          .pipe(takeUntil(this._destroy$))
          .subscribe((result) => {
            this.navbarService.refreshBalance()
            this.exportRecords()
          })
        return
      }
      forkJoin(this.submitAutoSettleOption())
        .pipe(
          map((response) => response[0]),
          takeUntil(this._destroy$)
        )
        .subscribe((forms) => {
          this.service.forms.markAsPristine()
          forms.length
            ? this.service
                .getExportableCountsForEvent(this.source.initializeRequest.event)
                .pipe(takeUntil(this._destroy$))
                .subscribe((exportableCounts: ServerRepresentedSingleEventRegistrationsCount) => {
                  this.settled = Number(exportableCounts.settledCount)
                  this.unsettled = Number(exportableCounts.unsettledCount)
                  this.selected = Number(exportableCounts.totalCount)
                  this.order = this.generateFullOrder(exportableCounts, this.service.forms.value)
                  this.records = this.generateFullRecords(exportableCounts, this.service.forms.value)
                  this.settleAllRecords()
                })
            : this.settleAllRecords()
        })
    } else {
      this.exportRecords()
    }
  }
  /**
   * Fires settle order based on selection and close the dialogue
   */
  settle() {
    this.processing = true
    this.service
      .settleByRegistrations(this.order as OrderByRegistrations)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (result) => {
          this.processing = false
          this.navbarService.refreshBalance()
          this.close()
        },
        (error) => (this.processing = false)
      )
  }

  /**
   * Exports the settled records
   */
  private exportRecords() {
    this.eventService
      .export(this.source.initializeRequest.event)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          this.autoCheckProgress()
          this.processing = false
        },
        () => (this.processing = false)
      )
  }

  /**
   * Settle all available records
   */
  private settleAllRecords() {
    const order = this.order as OrderByForms
    const activeRecords = this.records.filter((o) => o.active).map((o) => o.id)
    order.forms = order.forms.filter((o) => activeRecords.includes(o.id))
    this.service
      .settleByForms(order)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (result) => {
          this.navbarService.refreshBalance()
          this.exportRecords()
        },
        (error) => {
          console.error(error.message)
          this.processing = false
        }
      )
  }

  /**
   * Check forms for auto_settle changes and update them
   * @returns Observable
   */
  private submitAutoSettleOption(): Observable<any>[] {
    this.service.forms.controls.forEach((o) => delete o.value.record)
    let observables: Observable<Partial<Persona>>[] = []
    if (!this.service.forms.pristine) {
      const changedForms = this.service.forms.controls.filter((o) => !o.pristine)
      observables = changedForms.map((form) => {
        return this.personaService.patchForm(form.value.id).pipe(
          catchError((error) => {
            console.error('Error in patch form for form ID:', form.value.id, 'Error:', error)
            return of(error)
          })
        )
      })
    }
    return observables.length > 0 ? observables : [of([])]
  }
  /**
   * Automatically checks the export progress and updates accordingly
   */
  autoCheckProgress() {
    const exportProgress$ = this.eventService.checkExportProgress(this.source.initializeRequest.event)
    const exportProgressPool$ = exportProgress$
      .pipe(
        expand((_) =>
          timer(3000).pipe(
            tap(() => (this.processing = true)),
            concatMap(() => exportProgress$)
          )
        )
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe((exportStatus: ExportStatus) => {
        this.exporting = true
        this.exportStatus = exportStatus

        if (exportStatus.status === 'running') {
          this.processing = false
        } else {
          exportProgressPool$.unsubscribe()
          this.processing = false
        }
      })
  }
  /**
   * Generates a complete list of available orders for a selected event
   * @param counts ServerRepresentedSingleEventRegistrationsCount
   * @param personas Persona[]
   * @returns OrderByForms
   */
  generateFullOrder(counts: ServerRepresentedSingleEventRegistrationsCount, personas: Persona[]): OrderByForms {
    const forms = Object.keys(counts.forms)
    const formOrders = []
    const currentPlan = this.auth.plan
    for (const form of forms) {
      const order = { id: personas.find((o) => o.slug === form)?.id, orders: [] }
      if (!counts?.forms[form]?.unsettled) {
        const settledCount = this.calculateTotal(counts?.forms[form]?.settled)
        this.settledForms.push({ count: settledCount, form: form })
        this.settledTotal = this.settledTotal + settledCount
        continue
      }
      const types = Object.keys(counts.forms[form].unsettled)
      for (const type of types) {
        if (type === 'free') {
          continue
        }
        order.orders.push({
          price_id: this.plans[currentPlan][type].id,
          quantity: counts.forms[form].unsettled[type],
          type: type
        })
      }
      if (order.id) formOrders.push(order)
    }
    return {
      forms: formOrders
    }
  }
  /**
   * Generates a complete list of records for an event in case of settling by forms
   * @param counts ServerRepresentedSingleEventRegistrationsCount
   * @param personas Persona[]
   * @returns Record[]
   */
  generateFullRecords(counts: ServerRepresentedSingleEventRegistrationsCount, personas: Persona[]): Record[] {
    const currentPlan = this.auth.plan
    const formOrders = this.order as OrderByForms
    const records = []
    formOrders.forms.forEach((formOrder) => {
      const persona = personas.find((o) => o.id === formOrder.id)
      const breakdownRecords: Record[] = formOrder.orders.map((order: Order) => {
        const price = this.plans[currentPlan][order.type].amount
        const priceCompared = this.plans.Pro[order.type].amount
        return {
          title: `${persona.name} ${this.getTitle(order)}`,
          id: persona.id,
          canBeActivated: false,
          active: false,
          selected: order.quantity,
          unsettled: order.quantity,
          settled: this.calculateTotal(counts.forms[persona.slug]?.settled),
          eachAt: price / 100,
          eachAtCompared: priceCompared / 100,
          price: (order.quantity * price) / 100,
          priceCompared: (order.quantity * priceCompared) / 100
        }
      })
      const mainRecord: Record = {
        title: `${persona.name}`,
        id: persona.id,
        canBeActivated: true,
        active: persona.slug === this.selectedForm ? true : this.selectedForm ? false : true,
        settled: breakdownRecords[0].settled,
        unsettled: breakdownRecords.reduce((p, c) => p + c.unsettled, 0),
        price: breakdownRecords.reduce((p, c) => p + c.price, 0),
        priceCompared: breakdownRecords.reduce((p, c) => p + c.priceCompared, 0),
        breakdown: breakdownRecords,
        viewBreakdown: false
      }
      records.push(mainRecord)
    })
    return records
  }
  calculateTotal(settledObject: { [key: string]: number } | null | undefined): number {
    if (!settledObject || typeof settledObject !== 'object') return 0

    return Object.values(settledObject).reduce((sum, value) => sum + Number(value), 0)
  }

  /**
   * Gets the final total price
   */
  get totalPrice(): number {
    return this.records.filter((o) => o.active).reduce((p, c) => p + c.price, 0)
  }
  /**
   * Gets the final total compared price (On Pro Plan) in case of Basic Plan
   */
  get totalPriceCompared(): number {
    return this.records.filter((o) => o.active).reduce((p, c) => p + c.priceCompared, 0)
  }
  /**
   * Whether the user can settle selected registrations (requires sufficient balance).
   */
  get canSettleProceed(): boolean {
    if (this.insufficientBalance) return false
    return this.totalPrice > 0 && this.roundedCustomerBalance >= this.roundedTotalPrice
  }

  /**
   * Whether the user can Pay & Export, Export, or Top up.
   */
  get canPayExportProceed(): boolean {
    if (this.insufficientBalance) {
      return true
    }
    return (this.totalPrice > 0 && this.roundedCustomerBalance >= this.roundedTotalPrice) || (this.settled > 0 && (this.records.every((o) => !o.active) || this.registrantMode))
  }
  /** Gets the savings a user can obtain if he upgrades the plan */
  get savings() {
    return this.totalPrice - this.totalPriceCompared
  }
}
